import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, MapPin, Clock, Mic } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'sonner';

export default function ItineraryTab({ event, isOrganizer }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newDay, setNewDay] = useState({ title: '', date: event.start_date });
  const [stopForms, setStopForms] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/events/${event.id}/itinerary`);
      setDays(r.data || []);
    } catch (err) {
      console.error('Failed to load itinerary:', err);
    } finally {
      setLoading(false);
    }
  }, [event.id]);
  useEffect(() => { load(); }, [load]);

  const addDay = async () => {
    if (!newDay.date) return toast.error('Pick a date');
    try {
      await api.post(`/events/${event.id}/itinerary/days`, newDay);
      setNewDay({ title: '', date: event.start_date });
      load();
    } catch (err) {
      console.error('Failed to add day:', err);
      toast.error('Failed to add day');
    }
  };

  const deleteDay = async (dayId) => {
    if (!window.confirm('Delete this day and all its stops?')) return;
    try {
      await api.delete(`/events/${event.id}/itinerary/days/${dayId}`);
      load();
    } catch (err) {
      console.error('Failed to delete day:', err);
      toast.error('Failed to delete day');
    }
  };

  const addStop = async (dayId) => {
    const f = stopForms[dayId] || {};
    if (!f.title) return toast.error('Stop title required');
    try {
      await api.post(`/events/${event.id}/itinerary/days/${dayId}/stops`, { ...f, type: f.type || 'stop' });
      setStopForms((s) => ({ ...s, [dayId]: { title: '', time: '', location_text: '', notes: '', type: 'stop' } }));
      load();
    } catch (err) {
      console.error('Failed to add stop:', err);
      toast.error('Failed to add stop');
    }
  };

  const deleteStop = async (stopId) => {
    try {
      await api.delete(`/events/${event.id}/itinerary/stops/${stopId}`);
      load();
    } catch (err) {
      console.error('Failed to delete stop:', err);
      toast.error('Failed to delete stop');
    }
  };

  return (
    <div className="space-y-3" data-testid="itinerary-tab">
      {isOrganizer && (
        <Card className="rounded-2xl p-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Title</Label>
              <Input value={newDay.title} onChange={(e) => setNewDay((d) => ({ ...d, title: e.target.value }))} placeholder="Day title" data-testid="itinerary-day-title" />
            </div>
            <div>
              <Label className="text-xs">Date</Label>
              <Input type="date" value={newDay.date} onChange={(e) => setNewDay((d) => ({ ...d, date: e.target.value }))} data-testid="itinerary-day-date" />
            </div>
            <Button onClick={addDay} data-testid="itinerary-add-day" className="rounded-xl gradient-fab text-white"><Plus className="h-4 w-4" /></Button>
          </div>
        </Card>
      )}
      {loading && <div className="h-32 rounded-2xl bg-secondary animate-pulse" />}
      {!loading && days.length === 0 && (
        <Card className="rounded-2xl p-6 text-center text-sm text-muted-foreground">No itinerary yet. {isOrganizer ? 'Add the first day above.' : 'Check back soon.'}</Card>
      )}
      {days.map((d, idx) => (
        <Card key={d.id} className="rounded-2xl overflow-hidden" data-testid={`day-${d.id}`}>
          <div className="px-4 py-3 flex items-center justify-between bg-secondary/50">
            <div>
              <div className="font-serif text-lg font-semibold">Day {d.day_number ?? idx+1} · {d.title || 'Itinerary'}</div>
              <div className="text-xs text-muted-foreground">{d.date}</div>
            </div>
            {isOrganizer && <Button variant="ghost" size="icon" onClick={() => deleteDay(d.id)} data-testid={`delete-day-${d.id}`} className="rounded-xl text-destructive"><Trash2 className="h-4 w-4" /></Button>}
          </div>
          <div className="divide-y">
            {(d.stops || []).map((s) => (
              <div key={s.id} className="p-3 flex items-start gap-3" data-testid={`stop-${s.id}`}>
                <div className="h-9 w-9 rounded-xl bg-secondary flex items-center justify-center mt-0.5">
                  {s.type === 'session' ? <Mic className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-medium text-sm">{s.title}</div>
                    {s.time && <span className="text-[11px] text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" />{s.time}</span>}
                    {s.type !== 'stop' && <span className="text-[10px] uppercase font-semibold text-[hsl(var(--accent))]">{s.type}</span>}
                  </div>
                  {s.location_text && <div className="text-xs text-muted-foreground mt-0.5">{s.location_text}</div>}
                  {s.speaker_name && <div className="text-xs text-muted-foreground mt-0.5">Speaker: {s.speaker_name}</div>}
                  {s.notes && <div className="text-xs text-muted-foreground mt-1 line-clamp-3">{s.notes}</div>}
                </div>
                {isOrganizer && <Button variant="ghost" size="icon" onClick={() => deleteStop(s.id)} data-testid={`delete-stop-${s.id}`} className="rounded-xl text-destructive"><Trash2 className="h-4 w-4" /></Button>}
              </div>
            ))}
          </div>
          {isOrganizer && (
            <div className="p-3 bg-card border-t space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Title" value={stopForms[d.id]?.title || ''} onChange={(e) => setStopForms((s) => ({ ...s, [d.id]: { ...s[d.id], title: e.target.value } }))} data-testid={`stop-title-${d.id}`} />
                <Input placeholder="Time (e.g., 09:00)" value={stopForms[d.id]?.time || ''} onChange={(e) => setStopForms((s) => ({ ...s, [d.id]: { ...s[d.id], time: e.target.value } }))} data-testid={`stop-time-${d.id}`} />
              </div>
              <Input placeholder="Location (optional)" value={stopForms[d.id]?.location_text || ''} onChange={(e) => setStopForms((s) => ({ ...s, [d.id]: { ...s[d.id], location_text: e.target.value } }))} data-testid={`stop-location-${d.id}`} />
              <div className="flex gap-2">
                <select value={stopForms[d.id]?.type || 'stop'} onChange={(e) => setStopForms((s) => ({ ...s, [d.id]: { ...s[d.id], type: e.target.value } }))} className="flex-1 px-3 rounded-xl border border-input bg-card text-sm h-10" data-testid={`stop-type-${d.id}`}>
                  <option value="stop">Stop</option>
                  <option value="session">Session</option>
                  <option value="break">Break</option>
                </select>
                {(stopForms[d.id]?.type === 'session') && (
                  <Input placeholder="Speaker name" value={stopForms[d.id]?.speaker_name || ''} onChange={(e) => setStopForms((s) => ({ ...s, [d.id]: { ...s[d.id], speaker_name: e.target.value } }))} data-testid={`stop-speaker-${d.id}`} />
                )}
                <Button onClick={() => addStop(d.id)} data-testid={`add-stop-${d.id}`} className="rounded-xl gradient-fab text-white"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}
